/**
 * 아이콘 관리 — 커스텀 SVG 아이콘 레지스트리(명칭·이미지·저장위치) + AI SVG 생성.
 * 저장 위치: workchat Postgres `admin_icon_registry`(svg TEXT). 메뉴 관리의 아이콘 피커에서 선택 가능.
 */
import { useEffect, useState } from 'react';
import './ServiceModules.css';
import { Trash2, X, Plus, Sparkles, Loader2 } from '../../../icons';
import {
  createIcon, deleteIcon, generateIconSvg,
  type IconRow,
} from '../../../api/icons';
import { useCustomIcons, refreshCustomIcons } from './iconStore';

/** ⚠ 2026-09-09 — 새 목록 규격 화면(IconListSection)이 그대로 쓴다. 두 벌로 그리지 않는다. */
export function SvgPreview({ svg, size = 28 }: { svg: string; size?: number }) {
  if (!svg || !/<svg[\s\S]*<\/svg>/i.test(svg)) {
    return <span className="svc-svgicon svc-svgicon--empty" style={{ width: size, height: size }} />;
  }
  return <span className="svc-svgicon" style={{ width: size, height: size }} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export function IconRegistrySection() {
  // 공유 스토어 — 추가/삭제 시 메뉴 편집 등 모든 아이콘 피커에 즉시 반영.
  const { icons: list } = useCustomIcons();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { void refreshCustomIcons().finally(() => setLoading(false)); }, []);

  async function remove(ic: IconRow) {
    if (!window.confirm(`아이콘 "${ic.label}"(${ic.name})을 삭제할까요? 이 아이콘을 쓰는 메뉴는 기본 아이콘으로 표시됩니다.`)) return;
    setBusyId(ic.id);
    try { await deleteIcon(ic.id); await refreshCustomIcons(); }
    catch (e: any) { setError(e?.response?.data?.error ?? '삭제 실패'); }
    finally { setBusyId(null); }
  }

  return (
    <section className="svc-cat svc-cat--icon">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><Sparkles size={17} /></span>
        <span className="svc-cat__title">아이콘 관리</span>
        <span className="svc-cat__count">{list.length}</span>
        <span className="svc-cat__desc">커스텀 SVG 아이콘 (명칭·이미지·저장위치) · AI 생성</span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

      <button type="button" className="svc-add" onClick={() => setShowModal(true)}>
        <span className="svc-add__icon"><Plus size={20} /></span>
        <span>
          <span className="svc-add__title">아이콘 추가</span>
          <span className="svc-add__desc">SVG 를 붙여넣거나 AI 로 생성해 커스텀 아이콘을 등록합니다.</span>
        </span>
      </button>

      {loading ? (
        <p className="svc-empty">불러오는 중…</p>
      ) : list.length === 0 ? (
        <p className="svc-empty">등록된 커스텀 아이콘이 없습니다.</p>
      ) : (
        <div className="svc-grid">
          {list.map((ic) => {
            const busy = busyId === ic.id;
            return (
              <div key={ic.id} className="svc-card">
                <div className="svc-card__top">
                  <span className="svc-card__icon"><SvgPreview svg={ic.svg} size={22} /></span>
                  <span className="svc-card__name" title={ic.label}>{ic.label}</span>
                  <button type="button" className="svc-btn svc-btn--danger" disabled={busy} onClick={() => void remove(ic)} aria-label="삭제">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="svc-card__chips">
                  <span className="svc-card__chip">custom:{ic.name}</span>
                  <span className="svc-card__chip">{ic.source === 'ai' ? 'AI 생성' : '수동'}</span>
                  <span className="svc-card__chip">DB 저장</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && <IconModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); void refreshCustomIcons(); }} />}
    </section>
  );
}

/** ⚠ 2026-09-09 — 「아이콘 등록」 폼은 이것 한 벌뿐이다. 새 목록 화면이 그대로 연다. */
export function IconModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [svg, setSvg] = useState('');
  const [prompt, setPrompt] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [genModel, setGenModel] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const validSvg = /<svg[\s\S]*<\/svg>/i.test(svg);
  const validName = /^[a-z0-9][a-z0-9_-]*$/i.test(name.trim());

  async function aiGenerate() {
    if (!prompt.trim()) return;
    setGenBusy(true); setErr(null); setGenModel(null);
    try {
      const r = await generateIconSvg(prompt.trim());
      if (r.ok && r.svg) { setSvg(r.svg); setGenModel(r.model ?? null); }
      else setErr('AI 생성 실패');
    } catch (e: any) { setErr(e?.response?.data?.error ?? e?.message ?? 'AI 생성 실패'); }
    finally { setGenBusy(false); }
  }
  async function save() {
    setSaving(true); setErr(null);
    try {
      await createIcon({ name: name.trim(), label: label.trim() || name.trim(), svg, source: genModel ? 'ai' : 'manual', prompt: genModel ? prompt.trim() : null });
      onSaved();
    } catch (e: any) { setErr(e?.response?.data?.error ?? e?.message ?? '저장 실패'); }
    finally { setSaving(false); }
  }

  return (
    <div className="svc-modal__backdrop" role="presentation" onClick={onClose}>
      <div className="svc-modal" role="dialog" aria-modal="true" aria-label="아이콘 추가" onClick={(e) => e.stopPropagation()}>
        <div className="svc-modal__head">
          <h3>아이콘 추가</h3>
          <button type="button" className="svc-modal__close" onClick={onClose} aria-label="닫기"><X size={16} /></button>
        </div>
        <div className="svc-modal__body">
          {/* 미리보기 — 생성 중에는 스피너 */}
          <div className={`svc-iconprev${genBusy ? ' is-loading' : ''}`}>
            {genBusy
              ? <span className="svc-svgicon"><Loader2 size={40} className="svc-spin" /></span>
              : <SvgPreview svg={svg} size={40} />}
            <div className="svc-iconprev__meta">
              <span>{genBusy ? 'AI가 아이콘을 생성하는 중…' : (validSvg ? '미리보기' : 'SVG 미입력')}</span>
              {genBusy && <span className="svc-iconprev__bar"><span className="svc-iconprev__barfill" /></span>}
              {!genBusy && genModel && <span className="svc-card__chip">AI: {genModel}</span>}
            </div>
          </div>

          {/* AI 생성 */}
          <div className="svc-field">
            <label>AI 로 생성</label>
            <div className="svc-aigen">
              <input value={prompt} placeholder="예: 종이비행기, 차트 막대, 톱니바퀴…" onChange={(e) => setPrompt(e.target.value)} />
              <button type="button" className="svc-mbtn svc-mbtn--primary" disabled={genBusy || !prompt.trim()} onClick={() => void aiGenerate()}>
                {genBusy ? <Loader2 size={14} className="svc-spin" /> : <Sparkles size={14} />}
                {genBusy ? '생성 중…' : '생성'}
              </button>
            </div>
            <span className="svc-field__hint">활성 AI Provider 로 24x24 라인 SVG 를 생성합니다. 생성 후 아래에서 수정 가능.</span>
          </div>

          <div className="svc-row2">
            <div className="svc-field">
              <label>이름(키) *</label>
              <input value={name} placeholder="영문/숫자/하이픈 (예: rocket)" onChange={(e) => setName(e.target.value)} />
              {!validName && name && <span className="svc-field__hint" style={{ color: 'var(--color-danger,#dc2626)' }}>영문/숫자/하이픈만</span>}
            </div>
            <div className="svc-field">
              <label>표시 명칭</label>
              <input value={label} placeholder="예: 로켓" onChange={(e) => setLabel(e.target.value)} />
            </div>
          </div>
          <div className="svc-field">
            <label>SVG 본문</label>
            <textarea rows={6} value={svg} spellCheck={false} placeholder="<svg ...>...</svg> 붙여넣기 또는 AI 생성 결과"
              style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text)', resize: 'vertical' }}
              onChange={(e) => setSvg(e.target.value)} />
            <span className="svc-field__hint">저장 시 script/이벤트핸들러는 자동 제거됩니다. 저장 위치: 서버 DB(admin_icon_registry).</span>
          </div>

          {err && <div className="svc-test svc-test--bad" role="alert"><span className="svc-dot svc-dot--bad" />{err}</div>}
        </div>
        <div className="svc-modal__foot">
          <span className="spacer" />
          <button type="button" className="svc-mbtn" onClick={onClose}>취소</button>
          <button type="button" className="svc-mbtn svc-mbtn--primary" onClick={() => void save()} disabled={!validName || !validSvg || saving}>
            {saving ? '저장 중…' : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
}
